import type { ConfigurationStats } from "./configurationTypes";
import { ChefHat, Leaf, DollarSign, TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";
import { StatsCard } from "../StatsCard";

interface ConfigurationStatsProps {
  stats: ConfigurationStats;
}

// Component for just the stats cards
export function ConfigurationStatsCards({ stats }: ConfigurationStatsProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 min-w-0">
      <StatsCard
        label="Articles du menu"
        value={stats.totalRecettes}
        description={`${stats.recettesConfigurees} avec recette`}
        icon={ChefHat}
        iconColor="var(--color-steel)"
      />

      <StatsCard
        label="Ingrédients utilisés"
        value={stats.ingredientsUniques}
        description="Ingrédients uniques"
        icon={Leaf}
        iconColor="var(--color-olive)"
      />

      <StatsCard
        label="Coût moyen"
        value={
          stats.coutMoyen > 0
            ? stats.coutMoyen.toLocaleString("fr-CA", {
                style: "currency",
                currency: "CAD",
              })
            : "N/A"
        }
        description="Par recette"
        icon={DollarSign}
        iconColor="var(--color-brick)"
      />

      <StatsCard
        label="Marge moyenne"
        value={
          stats.margeMoyenne > 0
            ? `${stats.margeMoyenne.toFixed(0)}%`
            : "N/A"
        }
        description={
          stats.margeMoyenne > 0
            ? stats.margeMoyenne >= 50
              ? "Excellente marge"
              : stats.margeMoyenne >= 30
              ? "Bonne marge"
              : "Marge faible"
            : "Aucune donnée"
        }
        icon={TrendingUp}
        iconColor={
          stats.margeMoyenne >= 50
            ? "var(--color-olive)"
            : stats.margeMoyenne >= 30
            ? "var(--color-amber)"
            : "var(--color-brick)"
        }
      />
    </div>
  );
}

// Component for the progress bar
export function ConfigurationProgressBar({ stats }: ConfigurationStatsProps) {
  const progressPercent = stats.totalRecettes > 0
    ? (stats.recettesConfigurees / stats.totalRecettes) * 100
    : 0;

  const isFullyConfigured = stats.recettesNonConfigurees === 0 && stats.totalRecettes > 0;

  return (
    <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isFullyConfigured ? (
            <CheckCircle2 className="w-5 h-5 text-[var(--color-olive)]" />
          ) : (
            <ChefHat className="w-5 h-5 text-[var(--color-steel)]" />
          )}
          <h3 className="font-semibold text-[var(--foreground)]">
            {isFullyConfigured ? "Tous les articles ont une recette" : "Recettes à compléter"}
          </h3>
        </div>
        <span className="text-sm font-medium text-[var(--foreground)]">
          {stats.recettesConfigurees} / {stats.totalRecettes}
        </span>
      </div>
      
      <div className="w-full h-3 bg-[var(--muted)] rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${
            isFullyConfigured 
              ? "bg-gradient-to-r from-[var(--color-olive)] to-[var(--color-olive-light)]"
              : "bg-gradient-to-r from-[var(--color-steel)] to-[var(--color-steel-light)]"
          }`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      <div className="flex items-center justify-between mt-2">
        {isFullyConfigured ? (
          <span className="text-xs text-[var(--color-olive)] flex items-center gap-1 font-medium">
            <CheckCircle2 className="w-3 h-3" />
            Tous les articles du menu ont une recette !
          </span>
        ) : (
          <>
            <span className="text-xs text-[var(--muted-foreground)]">
              {progressPercent.toFixed(0)}% complété
            </span>
            {stats.recettesNonConfigurees > 0 && (
              <span className="text-xs text-[var(--color-amber)] flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {stats.recettesNonConfigurees} sans recette
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Legacy component for backward compatibility - now just combines both
export function ConfigurationStatsComponent({ stats }: ConfigurationStatsProps) {
  return (
    <div className="space-y-4">
      <ConfigurationProgressBar stats={stats} />
      <ConfigurationStatsCards stats={stats} />
    </div>
  );
}